import { useMemo, useRef } from "react";
import { ApolloProvider } from "@apollo/client";
import { createRoot, Root } from "react-dom/client";
import { Redirect, Router, Switch, Route } from "react-router-dom";
import { DexApp } from "@jsq/dex-components";
import { ErrorBoundary, ToastContainer } from "@jsq/ds/core";
import {
  FeatureFlagProvider,
  useFeatureFlag,
  useIdentifyArena,
  useUpdateContext,
} from "@jsq/flags-react";
import { init, setUser } from "@jsq/observability";
import "@jsq/ds/index.css";
import { client } from "~/apollo/client";
import { Layout } from "~/components/Layout/Layout";
import { DocumentVaultComponent } from "~/pages/DocumentVault/DocumentVault";
import { FayAI } from "~/pages/Fay";
import { FundAccounting } from "~/pages/FundAccounting/FundAccounting";
import { NotFound } from "~/pages/NotFound";
import history from "~/utils/history";
import { ErrorPage } from "./components/ErrorPage/ErrorPage";
import { PrivateRoute } from "./components/PrivateRoute";
import { AppProvider, useAppContext } from "./contexts/appContext";
import { globalEnv } from "./global-env";
import { Analytics } from "./pages/Analytics";
import { Billing } from "./pages/Billing";
import { CCTools } from "./pages/CCTools";
import { Compliance } from "./pages/Compliance";
import { ClientsView } from "./pages/ControlTower/ClientsView";
import { IndividualsView } from "./pages/ControlTower/IndividualsView";
import { WorkflowView } from "./pages/ControlTower/WorkflowView";
import { Dashboard } from "./pages/Dashboard";
import { DataStudio } from "./pages/DataStudio";
import DataVault from "./pages/DataVault";
import { DdqIntake } from "./pages/ddq/intake";
import { Enablement } from "./pages/Enablement";
import { Login } from "./pages/Login";
import { Reporting } from "./pages/Reporting";
import { ServiceRequests } from "./pages/ServiceRequests/ServiceRequests";
import Treasury from "./pages/Treasury";
import UsersAndRoles from "./pages/UserAndRoles";
import { setupCoordinatedLogout } from "./utils/coordinateLogout";
import { initializeHeap } from "./utils/heap";
import {
  canAccessFayAI,
  canAccessManagerView,
  getRedirectPath,
  isAdminUser,
  isControlTowerUser,
  isDataVaultAdmin,
  isDataVaultUser,
  isDexUser,
  isEnablementUser,
  isGpWorkspaceAccessProvision,
  isDdqProdOpsUser,
  isServiceRequestAdmin,
} from "./utils/userRoles";

type RootedGlobalThings = typeof globalThis & {
  __JSQ_ROOT__?: Root;
};

// Initialize our observability library
init({
  applicationId: __DATADOG_RUM_APP_ID__,
  clientToken: __DATADOG_RUM_CLIENT_TOKEN__,
  serviceName: "canopy",
  version: __JSQ_COMMIT_HASH__,
});
initializeHeap();
setupCoordinatedLogout();

const AppLayout = () => {
  const updateFeatureFlagContext = useUpdateContext();
  const canopyWorkspaceAccessFF = useFeatureFlag("canopyWorkspaceAccess", false);
  const reportingFF = useFeatureFlag("reportsPDFM1", false);
  const fayAIInCanopyFF = useFeatureFlag("showFayAIInCanopy", false);
  const showDexFF = useFeatureFlag("showDex", false);
  const showDocumentVaultFF = useFeatureFlag("showDocumentVault", false);
  const ddqMvpFF = useFeatureFlag("ddqMvp", false);
  const routeOptions = useMemo(
    () => ({ featureFlags: { canopyWorkspaceAccessFF } }),
    [canopyWorkspaceAccessFF],
  );
  const { user } = useAppContext();

  const isManagerViewEnabled = useFeatureFlag("showManagerView", false);
  const hasWorkflowAccess = isControlTowerUser(user.roles);
  const hasManagerAccess = canAccessManagerView(user.roles);
  const showManagerTabs = isManagerViewEnabled && hasManagerAccess;

  const controlTowerRedirectPath = (() => {
    if (hasWorkflowAccess) {
      return `${globalEnv.controlTowerRoute}/workflows`;
    }
    if (showManagerTabs) {
      return `${globalEnv.controlTowerRoute}/clients`;
    }
    return getRedirectPath(user, routeOptions);
  })();

  // if user is not logged in, show error page
  if (user.id === -1) {
    return <ErrorPage />;
  }

  // init user for observability
  setUser({
    id: user.id.toString(),
  });
  void updateFeatureFlagContext({
    userId: user.id,
  });

  return (
    <Layout>
      <Switch>
        <Route exact path="/">
          {/** TODO: Add RBAC to redirect based on a user's roles (i.e.: FA, Payments, etc.) */}
          <Redirect to={getRedirectPath(user, routeOptions)} />
        </Route>
        {canopyWorkspaceAccessFF && (
          <Route path={globalEnv.yourWorkspacesRoute}>
            <Dashboard />
          </Route>
        )}
        <Route path={globalEnv.fundAccountingRoute}>
          <FundAccounting userId={user.id} />
        </Route>
        <Route path={globalEnv.fayAIRoute}>
          {canAccessFayAI(user.roles, fayAIInCanopyFF) ? (
            <FayAI />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.dexRoute}>
          {showDexFF && isDexUser(user.roles) ? (
            <DexApp />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.ddqIntakeRoute}>
          {ddqMvpFF && isDdqProdOpsUser(user.roles) ? (
            <DdqIntake />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.serviceRequestRoute}>
          {isServiceRequestAdmin(user) ? (
            <ServiceRequests />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.enablementRoute}>
          {isEnablementUser(user.roles) ? (
            <Enablement currentUser={user} />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.complianceRoute}>
          <Compliance currentUser={user} />
        </Route>
        <Route exact path={globalEnv.controlTowerRoute}>
          <Redirect to={controlTowerRedirectPath} />
        </Route>
        <Route path={`${globalEnv.controlTowerRoute}/workflows`}>
          {hasWorkflowAccess ? (
            <WorkflowView
              hasWorkflowAccess={hasWorkflowAccess}
              showManagerTabs={showManagerTabs}
            />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={`${globalEnv.controlTowerRoute}/clients`}>
          {showManagerTabs ? (
            <ClientsView
              hasWorkflowAccess={hasWorkflowAccess}
              showManagerTabs={showManagerTabs}
            />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={`${globalEnv.controlTowerRoute}/individuals`}>
          {showManagerTabs ? (
            <IndividualsView
              hasWorkflowAccess={hasWorkflowAccess}
              showManagerTabs={showManagerTabs}
            />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.treasuryRoute}>
          <Treasury currentUser={user} />
        </Route>
        <Route path={globalEnv.billingRoute}>
          <Billing currentUser={user} />
        </Route>
        <Route path={globalEnv.dataVaultRoute}>
          {isDataVaultUser(user.roles) ? (
            <DataVault currentUser={user} />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route path={globalEnv.dataStudioRoute}>
          <DataStudio currentUser={user} />
        </Route>
        {isAdminUser(user.roles) && (
          <Route path={globalEnv.ccToolsRoute}>
            <CCTools />
          </Route>
        )}
        <Route path={globalEnv.analyticsRoute}>
          <Analytics currentUser={user} />
        </Route>
        <Route path={globalEnv.reportingRoute}>
          {reportingFF ? <Reporting /> : <Redirect to={getRedirectPath(user, routeOptions)} />}
        </Route>
        {isGpWorkspaceAccessProvision(user.roles) && canopyWorkspaceAccessFF && (
          <Route path={globalEnv.userAndRolesRoute}>
            <UsersAndRoles />
          </Route>
        )}
        <Route path={globalEnv.documentVaultRoute}>
          {showDocumentVaultFF && isDataVaultAdmin(user.roles) ? (
            <DocumentVaultComponent />
          ) : (
            <Redirect to={getRedirectPath(user, routeOptions)} />
          )}
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Layout>
  );
};

const App = () => {
  const hasIdentifiedArena = useRef(false);
  const identifyArena = useIdentifyArena();

  if (!hasIdentifiedArena.current) {
    void identifyArena(0);
    hasIdentifiedArena.current = true;
  }

  return (
    <AppProvider>
      <Router history={history}>
        <Switch>
          <Route exact path="/login" component={Login} />
          <PrivateRoute path="/">
            <AppLayout />
          </PrivateRoute>
        </Switch>
      </Router>
    </AppProvider>
  );
};

const globalThisWithRoot: RootedGlobalThings = globalThis;

// In react 18 you should only create a root once
const root = (globalThisWithRoot.__JSQ_ROOT__ ??= createRoot(
  document.getElementById("jsq-app")!,
));

root.render(
  <ErrorBoundary isFullPage={true}>
    <ApolloProvider client={client}>
      <FeatureFlagProvider applicationName="canopy">
        <ToastContainer />
        <App />
      </FeatureFlagProvider>
    </ApolloProvider>
  </ErrorBoundary>,
);

if (process.env.NODE_ENV === "development") {
  if (module.hot) {
    module.hot.accept();
  }
}
